import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2, MessageCircle } from "lucide-react";

interface SupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DISCORD_WEBHOOK_URL = "https://discord.com/api/webhooks/1439869226390257737/6nDCoVK5O5S2894PID8c2iLAO23NibJ5Lp0w2MGgrnBVfSulod4JVVGJTzGQetwescav";

const supportTypes = [
  { value: "technical", label: "Technical Issue" },
  { value: "billing", label: "Billing & Credits" },
  { value: "feature", label: "Feature Request" },
  { value: "integration", label: "Integration Support" },
  { value: "general", label: "General Question" },
  { value: "other", label: "Other" },
];

const SupportDialog = ({ open, onOpenChange }: SupportDialogProps) => {
  const [supportType, setSupportType] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supportType || !email || !subject || !description) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Send to Discord webhook
      const supportTypeLabel = supportTypes.find(t => t.value === supportType)?.label || supportType;

      const webhookPayload = {
        embeds: [
          {
            title: "🎫 New Support Request",
            color: 0x5865F2, // Discord blue
            fields: [
              {
                name: "Support Type",
                value: supportTypeLabel,
                inline: true,
              },
              {
                name: "Email",
                value: email,
                inline: true,
              },
              {
                name: "Subject",
                value: subject,
                inline: false,
              },
              {
                name: "Description",
                value: description.length > 1024 ? description.substring(0, 1021) + "..." : description,
                inline: false,
              },
              {
                name: "Submitted At",
                value: new Date().toLocaleString(),
                inline: false,
              },
            ],
          },
        ],
      };

      const response = await fetch(DISCORD_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookPayload),
      });

      if (!response.ok) {
        throw new Error("Failed to submit support request");
      }

      toast({
        title: "Support Request Submitted",
        description: "We'll get back to you within 24 hours. Check your email for our response!",
      });

      // Reset form
      setSupportType("");
      setEmail("");
      setSubject("");
      setDescription("");
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting support request:", error);
      toast({
        title: "Submission Failed",
        description: "Please try again or email us directly at punit@flowlabs.live",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Contact Support
          </DialogTitle>
          <DialogDescription>
            Fill out the form below and we'll get back to you within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="support-type">Support Type *</Label>
            <Select value={supportType} onValueChange={setSupportType}>
              <SelectTrigger id="support-type">
                <SelectValue placeholder="Select support type" />
              </SelectTrigger>
              <SelectContent>
                {supportTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Your Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              placeholder="Brief description of your issue"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Please provide as much detail as possible..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              required
            />
          </div>

          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium">Need immediate help?</p>
            <p className="text-sm text-muted-foreground">
              Join our Discord community for community support and quick answers!
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.open("https://discord.gg/tU3REeD83u", "_blank")}
            >
              Join Discord Community
            </Button>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Request"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupportDialog;






